import useRequestSort from './useRequestSort';
import useMuiTablePaginate from './useMuiTablePaginate';
import {
  FilterType,
  TableBuilderCellProps,
  TableBuilderHeaderProps,
  TableBuilderPaginationProps,
} from 'types';
import { TableRow, TableCell } from '@mui/material';
import Table from 'components/Table';
import { ClickableRow } from 'styles';

type HookProps<T extends { id: string | number; selected?: boolean }> = {
  filter: FilterType;
  flatList: T[];

  cellProps: TableBuilderCellProps;

  headerProps: TableBuilderHeaderProps;

  paginationProps?: TableBuilderPaginationProps;

  isLoading?: boolean;

  _customRowProps?: Record<string, any>; // All cell will get access to these props

  tableMaxHeight?: string;

  onRowClick?: (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>, data: any) => void;
};

const noopFetchMore = () => {};

const useSimpleMuiTableBuilder = <T extends { id: string | number; selected?: boolean }>({
  filter,
  headerProps,
  cellProps,
  paginationProps = {},
  flatList = [],
  isLoading,
  _customRowProps,
  tableMaxHeight,
  onRowClick,
}: HookProps<T>) => {
  const { headCells, cellRenderer, getCellKey } = cellProps;
  const { tableTitle, renderOptions, filtersToShow } = headerProps;
  const {
    fetchMoreAction,
    totalSoFar = 0,
    hidePagination,
    rowsPerPageOptions,
    rowsPerPage,
    total,
    renderCustomPagination,
  } = paginationProps;

  const { onRequestSort } = useRequestSort({
    filter,
  });
  const { handleChangePage } = useMuiTablePaginate({
    filter,
    totalSoFar: totalSoFar || 0,
    fetchMoreAction: fetchMoreAction || noopFetchMore,
  });

  const renderRows = () => {
    return flatList.map((data) => {
      const { id } = data;

      const cellData = { ...data, ..._customRowProps };

      const RowComponent = onRowClick ? ClickableRow : TableRow;

      return (
        <RowComponent
          onClick={(e) => (onRowClick ? onRowClick(e, cellData) : null)}
          hover
          selected={data.selected}
          key={id}
        >
          {headCells.map((headCell) => {
            const cellKey = getCellKey ? getCellKey(cellData) : id;
            return (
              <TableCell key={`${cellKey} + ${headCell.id}`}>
                {cellRenderer[headCell.id]?.(cellData)}
              </TableCell>
            );
          })}
        </RowComponent>
      );
    });
  };

  const TableComponent = () => (
    <Table
      useDefaultTable
      headCells={headCells}
      hidePagination={hidePagination}
      tableTitle={tableTitle}
      renderOptions={renderOptions}
      renderRows={renderRows}
      onPageChange={handleChangePage}
      onRequestSort={onRequestSort}
      filter={filter}
      page={filter.page}
      count={total}
      rowsPerPage={rowsPerPage ?? filter.limit}
      rowsPerPageOptions={rowsPerPageOptions}
      isLoading={isLoading}
      renderCustomPagination={renderCustomPagination}
      tableMaxHeight={tableMaxHeight}
      filtersToShow={filtersToShow}
    />
  );

  return {
    TableComponent,
  };
};

export default useSimpleMuiTableBuilder;
